import noop from 'lodash/noop';
import { useMemo } from 'react';

import { HistoricalMedicalLeave, HistoricalMedicalLeaveQuery } from '@dreryk/egabinet-ezla-frontend-api';

import { useLazyLoadedDataConfigurationByCqrsQuery } from '@dreryk/drerykweb-main-frontend';
import {
   createInterpolatedTranslationKey,
   dateFormatter,
   dateTimeFormatter,
   TranslateFunction,
   useTranslate,
} from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   JustifyContent,
   LazyLoadedDataConfiguration,
   TableColumnConfiguration,
   TextPlaceholder,
   Tooltip,
   ValueRendererProps,
} from '@dreryk/react-components';

import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import { commonTranslationKeys, historicalLeavesTranslationKeys } from '../../../translations';
import { isInsuredInPoland } from '../common/common.utils';

import { DEFAULT_TABLE_ORDERINGS } from './historicalLeaves.constants';
import { HistoricalLeavePrintAction } from './HistoricalLeavePrintAction/HistoricalLeavePrintAction';
import { PueAccountStatus } from './PueAccountStatus/PueAccountStatus';

const payerAccessor = (translate: TranslateFunction) => (historicalMedicalLeave: HistoricalMedicalLeave): string => {
   if (historicalMedicalLeave.payerIdentificationNumber) {
      return translate(
         createInterpolatedTranslationKey(commonTranslationKeys.payerIdentificationNumberLabel, {
            identificationType: translate(
               commonTranslationKeys[historicalMedicalLeave.payerIdentificationNumber.identificationNumberType]
            ),
            identificationNumber: historicalMedicalLeave.payerIdentificationNumber.identificationNumber,
         })
      );
   }

   if (!isInsuredInPoland(historicalMedicalLeave.insuranceType)) {
      return translate(commonTranslationKeys[historicalMedicalLeave.insuranceType]);
   }

   return '-';
};

const columnsWidth = createDynamicColumnsWidth({
   actions: '37px',
   author: '33%',
   creationDateTime: '23%',
   daysCount: '45px',
   diagnosis: '60px',
   forGuardian: '85px',
   payer: '20%',
   payerPueAccount: '42px',
   periodOfValidity: '24%',
   serialNumber: '115px',
});

// eslint-disable-next-line max-lines-per-function
export const useHistoricalLeaveTableColumns = (): TableColumnConfiguration<HistoricalMedicalLeave>[] => {
   const translate = useTranslate();

   return useMemo<TableColumnConfiguration<HistoricalMedicalLeave>[]>(
      // eslint-disable-next-line max-lines-per-function
      () => [
         {
            label: historicalLeavesTranslationKeys.creationDateTime,
            width: columnsWidth.creationDateTime,
            accessor: 'creationDateTime',
            formatter: dateTimeFormatter,
            name: 'creationDateTime',
         },
         {
            accessor: (bean) => bean.createdBy,
            label: historicalLeavesTranslationKeys.documentAuthor,
            width: columnsWidth.author,

            renderer: ({ bean }: ValueRendererProps<HistoricalMedicalLeave>) => (
               <PractitionerSafePresenter
                  practitioner={bean.createdBy}
                  testId={'author'}
                  type={'standalone'}
                  withIdentification
               />
            ),
            name: 'createdBy',
         },
         {
            label: historicalLeavesTranslationKeys.periodOfValidity,
            width: columnsWidth.periodOfValidity,
            accessor: ({ dateFrom, dateTo }: HistoricalMedicalLeave) =>
               `${dateFormatter(dateFrom)} - ${dateFormatter(dateTo)}`,
            name: 'periodOfValidity',
            sortingDisabled: true,
         },
         {
            label: historicalLeavesTranslationKeys.daysCount,
            width: columnsWidth.daysCount,
            accessor: ({ daysCount }: HistoricalMedicalLeave) => daysCount,
            name: 'daysCount',
            sortingDisabled: true,
         },
         {
            label: historicalLeavesTranslationKeys.diagnosis,
            width: columnsWidth.diagnosis,
            accessor: ({ diagnosis }: HistoricalMedicalLeave) => diagnosis?.getCode().asString() ?? '',
            name: 'diagnosis',
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            label: historicalLeavesTranslationKeys.serialNumber,
            width: columnsWidth.serialNumber,
            accessor: ({ serialNumber }: HistoricalMedicalLeave) => serialNumber,
            name: 'serialNumber',
            sortingDisabled: true,
         },
         {
            label: historicalLeavesTranslationKeys.forGuardian,
            width: columnsWidth.forGuardian,
            accessor: ({ forGuardian }: HistoricalMedicalLeave) =>
               translate(historicalLeavesTranslationKeys[forGuardian ? 'yes' : 'no']),
            name: 'forGuardian',
            sortingDisabled: true,
         },
         {
            label: historicalLeavesTranslationKeys.payer,
            width: columnsWidth.payer,
            accessor: payerAccessor(translate),
            name: 'payer',
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            label: historicalLeavesTranslationKeys.payerPueAccount,
            width: columnsWidth.payerPueAccount,
            accessor: noop,
            name: 'pue',
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<HistoricalMedicalLeave>) => (
               <PueAccountStatus historicalMedicalLeave={bean} />
            ),
         },
         {
            label: '',
            width: columnsWidth.actions,
            accessor: noop,
            name: 'actions',
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<HistoricalMedicalLeave>) => (
               <Tooltip content={historicalLeavesTranslationKeys.print} testId={'print'}>
                  <HistoricalLeavePrintAction copyId={bean.copyId} originalId={bean.originalId} />
               </Tooltip>
            ),
            justifyContent: JustifyContent.END,
         },
      ],
      [translate]
   );
};

export const useHistoricalLeavesTableDataConfiguration = (
   patientId: number | undefined
): LazyLoadedDataConfiguration<HistoricalMedicalLeave> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<HistoricalMedicalLeave>(() => {
      const query = new HistoricalMedicalLeaveQuery();
      query.patientId = patientId as number;
      return query;
   }, DEFAULT_TABLE_ORDERINGS);
};
